(() => {
  /* Reveal on scroll */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('.sv-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* Rail arrows */
  const initRails = (root = document) => {
    root.querySelectorAll('[data-sv-rail]').forEach((wrap) => {
      if (wrap.dataset.svReady) return;
      wrap.dataset.svReady = '1';
      const rail = wrap.querySelector('.sv-rail');
      if (!rail) return;
      const step = () => (rail.firstElementChild ? rail.firstElementChild.getBoundingClientRect().width + 24 : rail.clientWidth);
      wrap.querySelector('[data-sv-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      wrap.querySelector('[data-sv-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    });
  };

  /* Tabs */
  const initTabs = (root = document) => {
    root.querySelectorAll('[data-sv-tabs]').forEach((tabs) => {
      if (tabs.dataset.svReady) return;
      tabs.dataset.svReady = '1';
      const buttons = tabs.querySelectorAll('[role="tab"]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          buttons.forEach((b) => b.setAttribute('aria-selected', b === btn ? 'true' : 'false'));
          tabs.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
            const active = panel.id === btn.getAttribute('aria-controls');
            panel.hidden = !active;
            if (active) panel.querySelectorAll('.sv-reveal').forEach((el) => el.classList.add('is-in'));
          });
        });
      });
    });
  };

  /* Quick add, uses Dawn's cart drawer / notification when present */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-sv-quick-add]');
    if (!form) return;
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (!cart || typeof cart.renderContents !== 'function') return; // fall back to normal form post
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const label = button.querySelector('span');
    const original = label.textContent;
    button.setAttribute('aria-busy', 'true');
    label.textContent = button.dataset.loading || '…';
    try {
      const body = new FormData(form);
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      cart.renderContents(data);
      // Dawn declares these with top-level const/function, so they are globals but not window props
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sv-quick-add', productVariantId: data.variant_id, cartData: data });
    } catch (err) {
      label.textContent = err.message && err.message.length < 40 ? err.message : 'Unavailable';
      setTimeout(() => (label.textContent = original), 2200);
      button.removeAttribute('aria-busy');
      return;
    }
    label.textContent = original;
    button.removeAttribute('aria-busy');
  });

  /* Fade slider (hero, testimonials) */
  const initFaders = (root = document) => {
    root.querySelectorAll('[data-sv-fader]').forEach((wrap) => {
      if (wrap.dataset.svReady) return;
      wrap.dataset.svReady = '1';
      const slides = [...wrap.querySelectorAll('[data-sv-slide]')];
      const dots = [...wrap.querySelectorAll('[data-sv-dot]')];
      if (slides.length < 2) return;
      let i = 0;
      let timer;
      const go = (n) => {
        i = (n + slides.length) % slides.length;
        slides.forEach((s, k) => {
          s.classList.toggle('is-active', k === i);
          s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
          s.querySelectorAll('a, button').forEach((el) => (el.tabIndex = k === i ? 0 : -1));
        });
        dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      };
      const play = () => { clearInterval(timer); timer = setInterval(() => go(i + 1), Number(wrap.dataset.svFader) || 6500); };
      dots.forEach((d, k) => d.addEventListener('click', () => { go(k); play(); }));
      wrap.querySelector('[data-sv-prev]')?.addEventListener('click', () => { go(i - 1); play(); });
      wrap.querySelector('[data-sv-next]')?.addEventListener('click', () => { go(i + 1); play(); });
      go(0);
      play();
    });
  };

  const init = (root) => {
    initReveal(root);
    initRails(root);
    initTabs(root);
    initFaders(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    // Content injected later (recommendations, filters) gets the same treatment
    new MutationObserver((records) => {
      records.forEach((r) => r.addedNodes.forEach((n) => { if (n.nodeType === 1) init(n.parentElement || n); }));
    }).observe(document.body, { childList: true, subtree: true });
  });

  /* Desktop header dropdowns open on hover (Dawn opens them on click only) */
  if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
    document.querySelectorAll('.header__inline-menu details').forEach((d) => {
      let timer;
      d.addEventListener('mouseenter', () => { clearTimeout(timer); d.setAttribute('open', ''); d.querySelector('summary')?.setAttribute('aria-expanded', 'true'); });
      d.addEventListener('mouseleave', () => { timer = setTimeout(() => { d.removeAttribute('open'); d.querySelector('summary')?.setAttribute('aria-expanded', 'false'); }, 150); });
    });
  }

  /* Filter drawer (mobile) + sort select */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-sv-drawer]');
    if (!t) return;
    const el = document.getElementById(t.dataset.svDrawer);
    if (!el) return;
    const open = !el.classList.contains('is-open');
    el.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('sv-lock', open);
  });
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-sv-sort]');
    if (!sel) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
